import { useEffect, useState } from "react";
import {
  Drawer,
  Typography,
  Upload,
  Button,
  Divider,
  Row,
  Col,
  message,
  Card,
  Spin,
  Tooltip,
  Select,
} from "antd";
import {
  InboxOutlined,
  DeleteOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  EditOutlined,
  CloseOutlined,
  MailOutlined,
} from "@ant-design/icons";
import TitleSubtitle from "../../../../components/titleSubtitle";
import { order } from "../../../../types";
import useUpload from "../../../../hooks/use-upload";
import { useUser } from "../../../../providers/user";
import { useMutation } from "@apollo/client";
import { CREATE_INVOICE, UPDATE_INVOICE } from "./requests";
import { invoiceStatus } from "../../../../utils/constants";

const { Title } = Typography;
const { Dragger } = Upload;
const { Option } = Select;

interface InvoiceDrawerProps {
  showModal: boolean;
  setShowModal: (value: boolean) => void;
  order: typeof order.order | null;
  refetch: () => void;
}

const InvoiceModal = ({ showModal, setShowModal, order, refetch }: InvoiceDrawerProps) => {
  const { upload } = useUpload();
  const { user } = useUser();
  const [fileXml, setFileXml] = useState<string | null>(null);
  const [filePdf, setFilePdf] = useState<string | null>(null);
  const [existsInvoice, setExistsInvoice] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [uploadLoading, setUploadLoading] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingUpdate, setLoadingUpdate] = useState<boolean>(false);
  const [invoiceStatusValue, setInvoiceStatusValue] = useState<string | undefined>(undefined);

  useEffect(() => {
    if(order?.invoice?.id){
      setExistsInvoice(true)
    }
    if (order?.invoice?.xml) {
      setFileXml(order?.invoice?.xml);
    }
    if (order?.invoice?.pdf) {
      setFilePdf(order?.invoice?.pdf);
    }
    if (order?.invoice?.status) {
      setInvoiceStatusValue(order.invoice.status);
    }
  }, [order]);

  const [createInvoice] = useMutation(CREATE_INVOICE);
  const [updateInvoice] = useMutation(UPDATE_INVOICE);

  const onClose = () => {
    setShowModal(false);
    setFilePdf(null);
    setFileXml(null);
    setExistsInvoice(false);
    setIsEditing(false);
  };

  const handleFileUpload = async (file: File, type: "xml" | "pdf") => {
    setUploadLoading(true);
    const fileName = `invoices/${order?.id}/${new Date().toISOString()}/${
      file?.name
    }`;

    const url = await upload(file, fileName);

    message.success("Imagen agregada.");

    if (type === "xml") setFileXml(url);
    if (type === "pdf") setFilePdf(url);
    setUploadLoading(false);
  };

  const handleFileDelete = (type: "xml" | "pdf") => {
    if (type === "xml") setFileXml(null);
    if (type === "pdf") setFilePdf(null);
  };

  const fiscal = order?.client?.fiscalData;
  const fullNameAddress = [
    fiscal?.address?.street,
    fiscal?.address?.exteriorNumber,
    fiscal?.address?.colony,
    fiscal?.address?.city,
    fiscal?.address?.state,
    fiscal?.address?.country,
    fiscal?.address?.postcode,
  ].filter(Boolean).join(", ");

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const payload = {
        variables: {
          invoice: {
            client: order?.client?.id,
            xml: fileXml,
            pdf: filePdf,
            emisor: user?.id,
            status: invoiceStatus.valid.value,
            location: order?.location?.id,
          },
          order:order?.id,
        },
      }

      await createInvoice(payload);
      await refetch();

      message.success("Factura guardada exitosamente");
      onClose();
    } catch (error) {
      console.log(error);
      message.error("Error al guardar la factura");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async () => {
    setLoadingUpdate(true);
    try {
      const payload = {
        variables: {
          id: order?.invoice?.id,
          invoice: {
            client: order?.client?.id,
            xml: fileXml,
            pdf: filePdf,
            emisor: user?.id,
            status: invoiceStatusValue,
          },
        },
      }

      await updateInvoice(payload);
      await refetch();

      message.success("Factura actualizada exitosamente");
      onClose();
    } catch (error) {
      console.log(error);
      message.error("Error al actualizar la factura");
    } finally {
      setLoadingUpdate(false);
    }
  };

  const handleSendEmail = () => {
    const clientEmail = order?.client?.fiscalData?.email;
    const senderEmail = "facturacion@polloloco.com";

    if (clientEmail) {
      console.log(`Enviando correo a ${clientEmail} desde ${senderEmail}`);
      message.success(`Correo enviado exitosamente a ${clientEmail}`);
    } else {
      message.error("No se encontró el correo del cliente");
    }
  };

  return (
    <Drawer title="Facturación" open={showModal} onClose={onClose} width={"60%"}>
      <Card
        style={{
          boxShadow: "rgba(0, 0, 0, 0.15) 1.95px 1.95px 2.6px",
          borderRadius: "15px",
        }}
        title={
          <div style={{ display: "flex",
            justifyContent: "space-between",
            alignItems: "center" }}>
            <div style={{ flex: 1 }}>
              <Title level={5} style={{ margin: 0 }}>Archivos de la factura</Title>
            </div>
            {existsInvoice && (
              <div style={{ display: "flex",
                justifyContent: "flex-end" }}>
                <Button
                  type="primary"
                  icon={<MailOutlined />}
                  onClick={handleSendEmail}
                  disabled={true}
                >
                  Enviar por Correo
                </Button>
                <Tooltip title={isEditing ? "Cancelar" : "Editar"}>
                  <Button
                    style={{ marginLeft: 10 }}
                    type="primary"
                    icon={isEditing ? <CloseOutlined /> : <EditOutlined />}
                    onClick={() => {
                      if (isEditing) {
                        setIsEditing(false);
                      } else {
                        setIsEditing(true);
                      }
                    }}
                  />
                </Tooltip>
              </div>
            )}
          </div>
        }
      >
        {isEditing ? (
          <div>
            <Select
              value={invoiceStatusValue}
              onChange={(value) => setInvoiceStatusValue(value)}
              style={{ width: "60%" }}
            >
              {Object.entries(invoiceStatus).map(([key, value]) => (
                <Option key={key} value={value.value}>
                  {value.name}
                </Option>
              ))}
            </Select>
          </div>
        ) : (
          order?.invoice?.status && (<TitleSubtitle
            title="Estatus"
            subtitle={
              order?.invoice?.status !== undefined
                ? invoiceStatus[order.invoice.status]?.name
                : "Sin estatus"
            }
          />
          )
        )}
        <Row gutter={[12, 12]}>
          <Col span={12}>
            {fileXml ? (
              <div>
                <Button
                  style={{ marginTop: 10 }}
                  type="link"
                  icon={<FileTextOutlined />}
                  onClick={() => window.open(fileXml, "_blank")}
                >
                  Ver archivo XML
                </Button>
                {(!existsInvoice || isEditing) && (
                  <Button
                    type="link"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => handleFileDelete("xml")}
                    style={{ marginLeft: 8 }}
                  >
                  Borrar
                  </Button>
                )}
              </div>
            ) : (
              <Spin spinning={uploadLoading}>
                <Dragger
                  beforeUpload={(file) => handleFileUpload(file, "xml")}
                  multiple={false}
                  accept=".xml"
                  showUploadList={false}
                  disabled={uploadLoading}
                  style={{ height: "120px",
                    marginBottom: "16px" }}
                >
                  <p className="ant-upload-drag-icon"><InboxOutlined /></p>
                  <p>Haz clic o arrastra un archivo XML</p>
                </Dragger>
              </Spin>
            )}
          </Col>

          <Col span={12}>
            {filePdf ? (
              <div>
                <Button
                  style={{ marginTop: 10 }}
                  type="link"
                  icon={<FilePdfOutlined />}
                  onClick={() => window.open(filePdf, "_blank")}
                >
                  Ver archivo PDF
                </Button>
                {(!existsInvoice || isEditing) && (
                  <Button
                    type="link"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => handleFileDelete("pdf")}
                    style={{ marginLeft: 8 }}
                  >
                    Borrar
                  </Button>
                )}
              </div>
            ) : (
              <Spin spinning={uploadLoading}>
                <Dragger
                  beforeUpload={(file) => handleFileUpload(file, "pdf")}
                  multiple={false}
                  accept=".pdf"
                  showUploadList={false}
                  disabled={uploadLoading}
                  style={{ height: "120px",
                    marginBottom: "16px" }}
                >
                  <p className="ant-upload-drag-icon"><InboxOutlined /></p>
                  <p>Haz clic o arrastra un archivo PDF</p>
                </Dragger>
              </Spin>
            )}
          </Col>
        </Row>
        {!existsInvoice && (
          <Button
            style={{ marginTop: 15 }}
            type="primary"
            disabled={!fileXml || !filePdf}
            loading={loading}
            onClick={handleSubmit}
          >
            Guardar factura
          </Button>
        )}
        {isEditing && (
          <Button
            style={{ marginTop: 15 }}
            type="primary"
            disabled={!fileXml || !filePdf}
            loading={loadingUpdate}
            onClick={handleEdit}
          >
            Actualizar Archivos de Factura
          </Button>
        )}
      </Card>

      <Divider />

      <Card
        style={{
          boxShadow: "rgba(0, 0, 0, 0.15) 1.95px 1.95px 2.6px",
          borderRadius: "15px",
        }}
        title={<Title level={5}>Datos fiscales del cliente</Title>}
      >
        {fiscal ? (
          <Row gutter={[12, 12]}>
            {fiscal?.name && (
              <Col span={24}><TitleSubtitle title="Nombre" subtitle={fiscal.name} /></Col>
            )}
            {fiscal?.rfc && (
              <Col span={12}><TitleSubtitle title="RFC" subtitle={fiscal.rfc} /></Col>
            )}
            {fiscal?.email && (
              <Col span={12}><TitleSubtitle title="Correo electrónico" subtitle={fiscal.email} /></Col>
            )}
            {fiscal?.regimenFiscal && (
              <Col span={24}><TitleSubtitle title="Régimen Fiscal" subtitle={fiscal.regimenFiscal} /></Col>
            )}
            {fiscal?.usoCFDI && (
              <Col span={24}><TitleSubtitle title="Uso CFDI" subtitle={fiscal.usoCFDI} /></Col>
            )}
            {fullNameAddress && (
              <Col span={24}><TitleSubtitle title="Dirección" subtitle={fullNameAddress} /></Col>
            )}
          </Row>
        ) : (
          <Typography.Text type="secondary">No hay datos fiscales disponibles.</Typography.Text>
        )}
      </Card>
      <Divider />
    </Drawer>
  );
};

export default InvoiceModal;
